import { keepPreviousData, useQuery } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

import { fetchProjectTemplate, fetchProjectTemplates, fetchProjectTemplateSummaries } from "./api"

export function useFetchProjectTemplates(
  bookIdOverride?: string,
  options?: { scope?: "company"; page?: number; search?: string; ownerBookId?: string; includeArchived?: boolean }
) {
  const getContext = useApiContext(bookIdOverride)
  const urlBookId = useBookId()
  const bookId = bookIdOverride ?? urlBookId
  const scopeSegment = options?.scope === "company" ? "company" : (bookId ?? "company")

  return useQuery({
    queryKey: ["project-templates", scopeSegment, options],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context")
      return fetchProjectTemplates(context, options)
    },
    placeholderData: options?.page ? keepPreviousData : undefined,
  })
}

export function useFetchProjectTemplateSummaries(
  bookIdOverride: string,
  options: { page: number; search?: string; ownerBookId?: string; includeArchived?: boolean }
) {
  const getContext = useApiContext(bookIdOverride)

  return useQuery({
    queryKey: ["project-templates", "summaries", bookIdOverride, options],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context")
      return fetchProjectTemplateSummaries(context, options)
    },
    placeholderData: keepPreviousData,
  })
}

export function useFetchProjectTemplate(templateId: string, enabled = true) {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["project-template", templateId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context")
      return fetchProjectTemplate(context, templateId)
    },
    enabled: enabled && !!templateId,
  })
}
